import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

export const DashboardSkeleton = () => (
  <div className="space-y-4 p-4">
    {/* Header skeleton */}
    <div className="bg-primary/5 p-4 rounded-lg">
      <Skeleton className="h-8 w-48 mb-2" />
      <Skeleton className="h-4 w-32" />
    </div>
    
    {/* Check-in button skeleton */}
    <Skeleton className="h-20 w-full rounded-2xl" />
    
    {/* Wait times card skeleton */}
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border rounded-lg p-3">
            <Skeleton className="h-5 w-24 mb-2" />
            <div className="space-y-2">
              <div className="flex justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-12" />
              </div>
              <div className="flex justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  </div>
);

export const ProfileSkeleton = () => (
  <div className="space-y-6 p-4">
    {/* User info skeleton */}
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <Skeleton className="w-16 h-16 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-8 w-12 mx-auto mb-1" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
          <div className="text-center p-3 bg-muted/50 rounded-lg">
            <Skeleton className="h-8 w-12 mx-auto mb-1" />
            <Skeleton className="h-4 w-20 mx-auto" />
          </div>
        </div>
      </CardContent>
    </Card>
    
    {/* Badges skeleton */}
    <Card>
      <CardHeader>
        <Skeleton className="h-6 w-32" />
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg border">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-6 w-16" />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  </div>
);

export const CheckInSkeleton = () => (
  <div className="space-y-6 p-4">
    {/* Photo upload skeleton */}
    <Card>
      <CardContent className="p-4">
        <div className="text-center space-y-3">
          <Skeleton className="h-6 w-48 mx-auto" />
          <Skeleton className="h-4 w-64 mx-auto" />
          <Skeleton className="h-14 w-full rounded-lg" />
        </div>
      </CardContent>
    </Card>
    
    {/* Form selections skeleton */}
    <div className="space-y-4">
      <div className="space-y-2">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-12 w-full rounded-lg" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-5 w-24" />
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      </div>
      <div className="space-y-2">
        <Skeleton className="h-5 w-28" />
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
          <Skeleton className="h-12 rounded-lg" />
        </div>
      </div>
    </div>
    
    {/* Check-in button skeleton */}
    <Skeleton className="h-16 w-full rounded-xl" />
  </div>
);

export const MapViewSkeleton = () => (
  <div className="h-screen bg-background">
    {/* Header skeleton */}
    <div className="p-4 border-b">
      <Skeleton className="h-8 w-40" />
    </div>
    
    {/* Map skeleton */}
    <div className="flex-1 bg-muted/20 relative">
      <Skeleton className="absolute inset-0" />
      {/* Simulated map pins */}
      {[1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          className="absolute"
          style={{
            top: `${20 + i * 15}%`,
            left: `${15 + i * 20}%`
          }}
        >
          <Skeleton className="w-8 h-8 rounded-full" />
        </div>
      ))}
    </div>
    
    {/* Bottom info panel skeleton */}
    <div className="p-4 border-t bg-background">
      <Skeleton className="h-6 w-48 mb-2" />
      <div className="grid grid-cols-3 gap-4">
        <div className="text-center">
          <Skeleton className="h-8 w-12 mx-auto mb-1" />
          <Skeleton className="h-4 w-16 mx-auto" />
        </div>
        <div className="text-center">
          <Skeleton className="h-8 w-12 mx-auto mb-1" />
          <Skeleton className="h-4 w-16 mx-auto" />
        </div>
        <div className="text-center">
          <Skeleton className="h-8 w-12 mx-auto mb-1" />
          <Skeleton className="h-4 w-16 mx-auto" />
        </div>
      </div>
    </div>
  </div>
);

export const ListSkeleton = ({ items = 5 }: { items?: number }) => (
  <div className="space-y-3">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-3 border rounded-lg">
        <Skeleton className="w-10 h-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-3 w-3/4" />
        </div>
        <Skeleton className="w-16 h-6 rounded" />
      </div>
    ))}
  </div>
);